import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import Global from '../../../store/global-selectors';
import { getPartitionNumber } from '../../../services/partition-service';
import { getPreferredLocale } from '../../../intl/get-intl';
import { useIsShipstationReturnsEnabled } from '../../../hooks/use-seller-setting';

const SS_PRODUCT_VERSION = 'v3';

const localeCodeMap: Record<string, string> = {
  en: 'en-US',
  es: 'es-US',
  fr: 'fr-FR',
  de: 'de-DE',
};

export const getMappedLocale = (locale: string): string => localeCodeMap[locale] || locale;

const formatPlanLevel = (subscriptionPlan: { planName?: string } | null): string => {
  if (!subscriptionPlan?.planName) return 'unknown';
  return subscriptionPlan.planName.replace(/\s/g, '').toLowerCase();
};

const formatDate = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  try {
    return DateTime.fromISO(dateString).toFormat('yyyy/MM/dd');
  } catch {
    return null;
  }
};

export const formatPriorConversation = (messages: { sender: string; content: string }[]): string =>
  messages
    .filter((msg) => msg.sender === 'user' || msg.sender === 'assistant')
    .map((msg) => {
      const prefix = msg.sender === 'user' ? 'Customer' : 'Shippy';
      return `${prefix}: ${msg.content}`;
    })
    .join('\n');

export type SierraVariables = {
  requesterName: string;
  requesterEmail: string;
  brand: string;
  sellerId: string;
  userId: string;
  planLevel: string;
  accountCountry: string;
  ssProductVersion: string;
  userAdmin: string;
  messagingOrigin: string;
  organizationIdSeller: string;
  appLanguage: string;
  ssPlanVolume: string;
  ssEnvironment: string;
  billingCycleDate: string;
  startDate: string;
  features: string;
  PRIOR_CONVERSATION: string;
};

export type UseSierraVariablesReturn = {
  /** Variables object for Sierra API calls */
  sierraVariables: SierraVariables;
  /** Mapped locale code (e.g., 'en-US') */
  mappedLocale: string;
};

/**
 * Hook that builds Sierra context variables from Redux state.
 * Extracts user, seller, and subscription info for Sierra API calls.
 */
export const useSierraVariables = (): UseSierraVariablesReturn => {
  const { name, email } = useSelector(Global.selectUserCurrentUserInfo) ?? {};
  const sellerId = useSelector(Global.selectSellerSellerId);
  const userId = useSelector(Global.selectUserCurrentUserId);
  const homeCountry = useSelector(Global.selectSellerHomeCountry);
  const subscriptionPlan = useSelector(Global.selectSellerSubscriptionPlanInfo);
  const activationDate = useSelector(Global.selectSellerActivationDate);
  const locale = useSelector(getPreferredLocale);
  const userHasAdmin = useSelector(Global.selectUserhasAdministrationRole);
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const organizationIdSeller = useSelector(Global.selectSellerZendeskOrganizationID) ?? '';
  const isShipstationReturnsEnabled = useIsShipstationReturnsEnabled();

  const mappedLocale = getMappedLocale(locale || 'en');

  const sierraVariables = useMemo(
    (): SierraVariables => ({
      requesterName: name || '',
      requesterEmail: email || '',
      brand: 'shipstation',
      sellerId: sellerId?.toString() ?? '',
      userId: userId?.toString() ?? '',
      planLevel: formatPlanLevel(subscriptionPlan),
      accountCountry: homeCountry?.toLowerCase() ?? 'us',
      ssProductVersion: SS_PRODUCT_VERSION,
      userAdmin: userHasAdmin?.toString() ?? 'unknown',
      messagingOrigin: 'app',
      organizationIdSeller: organizationIdSeller?.toString() ?? '',
      appLanguage: mappedLocale,
      ssPlanVolume: 'unknown',
      ssEnvironment: `ss${getPartitionNumber()}`,
      billingCycleDate: formatDate(planInfo?.billingCycleEndDateTimeUTC) ?? '',
      startDate: formatDate(activationDate) ?? '',
      features: JSON.stringify(isShipstationReturnsEnabled ? ['ShipstationReturns'] : []),
      PRIOR_CONVERSATION: '',
    }),
    [
      name,
      email,
      sellerId,
      userId,
      homeCountry,
      subscriptionPlan,
      activationDate,
      mappedLocale,
      userHasAdmin,
      planInfo,
      organizationIdSeller,
      isShipstationReturnsEnabled,
    ],
  );

  return {
    sierraVariables,
    mappedLocale,
  };
};
